import { ChangeDetectionStrategy, Component, Input, OnDestroy, inject } from '@angular/core'
import {
  DataContextBuilder,
  ElderSelectComponent,
  ElderToastService,
  Filter,
  IDataContext,
  Interval,
} from '@elderbyte/ngx-starter'
import { ZoneId } from '@js-joda/core'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject } from 'rxjs'
import { FacilitySchedule } from '../../../../facilities/scheduling/facility-schedule'
import { WorkSchedule } from '../../../../schedules/work-schedule'
import { WorkScheduleManagerComponent } from '../../../../schedules/work-schedule-manager/work-schedule-manager.component'
import { WorkCenterSchedule } from '../work-center-schedule'
import {
  WorkCenterScheduleFilter,
  WorkCenterScheduleService,
} from '../work-center-schedule.service'

import { MatFormField, MatLabel } from '@angular/material/form-field'
import { FacilityScheduleSelectDirective } from '../../../../facilities/scheduling/facility-schedule-select.directive'

@Component({
  selector: 'app-work-center-schedule-detail',
  templateUrl: './work-center-schedule-detail.component.html',
  styleUrls: ['./work-center-schedule-detail.component.scss'],
  imports: [
    MatFormField,
    MatLabel,
    ElderSelectComponent,
    FacilityScheduleSelectDirective,
    WorkScheduleManagerComponent,
    ElderTranslatePipe,
  ],
})
export class WorkCenterScheduleDetailComponent implements OnDestroy {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly workCenterId$ = new BehaviorSubject<string>(null)

  public readonly data: IDataContext<WorkCenterSchedule>

  public dummy: FacilitySchedule

  @Input()
  public facilityId: string

  private workCenterScheduleService = inject(WorkCenterScheduleService)

  private toastService = inject(ElderToastService)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    this.data = DataContextBuilder.start<WorkCenterSchedule>()
      .pageSize(50)
      .reloadOnLocalChanges()
      .autoStartOnFiltersAll([WorkCenterScheduleFilter.WORK_CENTER_IDS_KEY])
      .build(this.workCenterScheduleService)
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnDestroy(): void {
    if (this.data) {
      this.data.close()
    }
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  public get workCenterId(): string {
    return this.workCenterId$.getValue()
  }

  @Input()
  public set workCenterId(workCenterId: string) {
    this.workCenterId$.next(workCenterId)

    if (workCenterId) {
      this.filters = [WorkCenterScheduleFilter.workCenterId(workCenterId)]
    }
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public set filters(filters: Filter[]) {
    this.data.filter.updateFilters(filters)
  }

  public onAdd(schedule: FacilitySchedule): void {
    if (schedule) {
      this.dummy = schedule
      setTimeout(() => {
        this.dummy = null
      })

      const newSchedule = new WorkCenterSchedule(
        this.workCenterId,
        'Neu',
        schedule.validityInterval,
        schedule.weekSchedule
      )

      this.create(newSchedule)
    }
  }

  public onCreate(_: Event): void {
    const interval = Interval.tomorrow(ZoneId.systemDefault()).toInstantString()
    const schedule = new WorkCenterSchedule(this.workCenterId, 'Neu', interval)

    this.create(schedule)
  }

  public onDeleteAll(schedules: WorkSchedule[]): void {
    this.workCenterScheduleService.deleteAll(schedules as WorkCenterSchedule[]).subscribe({
      next: (_) => this.toastService.info('messages.deleting_successful'),
      error: (err) => this.toastService.error('messages.deleting_failed', err),
    })
  }

  public onDuplicate(schedule: WorkSchedule): void {
    const workCenterSchedule = schedule as WorkCenterSchedule

    const newSchedule = new WorkCenterSchedule(
      workCenterSchedule.workCenterId,
      'Kopie',
      workCenterSchedule.validityInterval,
      workCenterSchedule.weekSchedule
    )

    this.create(newSchedule)
  }

  public onSave(schedule: WorkSchedule): void {
    this.save(schedule as WorkCenterSchedule)
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private create(schedule: WorkCenterSchedule): void {
    this.workCenterScheduleService.create(schedule).subscribe({
      next: (_) => this.toastService.info('messages.persisting_successful'),
      error: (err) => this.toastService.error('messages.persisting_failed', err),
    })
  }

  private save(schedule: WorkCenterSchedule): void {
    this.workCenterScheduleService.update(schedule).subscribe({
      next: (_) => this.toastService.info('messages.persisting_successful'),
      error: (err) => this.toastService.error('messages.persisting_failed', err),
    })
  }
}
